import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { format, startOfWeek, addDays } from "date-fns";
import type { WeekTask } from "./use-week-tasks";

/**
 * Henter alle oppgaver med estimated_deadline fra og med starten av denne
 * uken og frem i tid. Brukes til ukegrupperte listevisninger (VIB-574).
 *
 * Inkluderer egne tildelinger pluss tildelinger på prosjekter brukeren er
 * PL på. Returnerer alle åpne statuser (alt som ikke er approved).
 */
export function useUpcomingTasks(
  userId: string | null,
  pmProjectIds?: string[],
  weeksAhead: number = 12,
) {
  const today = new Date();
  const monday = startOfWeek(today, { weekStartsOn: 1 });
  const horizon = addDays(monday, weeksAhead * 7);
  const fromStr = format(monday, "yyyy-MM-dd");
  const toStr = format(horizon, "yyyy-MM-dd");

  return useQuery({
    queryKey: ["upcoming-tasks", fromStr, toStr, userId, pmProjectIds?.join(",")],
    enabled: !!userId,
    queryFn: async (): Promise<WeekTask[]> => {
      if (!userId) return [];

      const { data: ownTasks, error: ownErr } = await supabase
        .from("budget_line_assignments")
        .select(
          "id, budget_line_id, hours, task_status, estimated_deadline, notes, user_id, budget_lines!inner(description, phase, project_id, projects(name, project_code))",
        )
        .eq("user_id", userId)
        .gte("estimated_deadline", fromStr)
        .lte("estimated_deadline", toStr)
        .not("task_status", "eq", "approved");
      if (ownErr) throw ownErr;

      let pmTasks: unknown[] = [];
      if (pmProjectIds && pmProjectIds.length > 0) {
        const { data, error } = await supabase
          .from("budget_line_assignments")
          .select(
            "id, budget_line_id, hours, task_status, estimated_deadline, notes, user_id, budget_lines!inner(description, phase, project_id, projects(name, project_code)), users!budget_line_assignments_user_id_fkey(full_name)",
          )
          .in("budget_lines.project_id", pmProjectIds)
          .gte("estimated_deadline", fromStr)
          .lte("estimated_deadline", toStr)
          .not("task_status", "eq", "approved")
          .neq("user_id", userId);
        if (error) throw error;
        pmTasks = data ?? [];
      }

      const ownUserIds = [...new Set((ownTasks ?? []).map((a: { user_id: string }) => a.user_id))];
      const userMap: Record<string, string> = {};
      if (ownUserIds.length > 0) {
        const { data: users } = await supabase
          .from("users")
          .select("id, full_name")
          .in("id", ownUserIds);
        for (const u of users ?? []) {
          userMap[u.id] = u.full_name ?? "Ukjent";
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapRow = (a: any): WeekTask => {
        const bl = a.budget_lines;
        return {
          id: a.id,
          budget_line_id: a.budget_line_id ?? "",
          project_id: bl?.project_id ?? "",
          project_name: bl?.projects?.name ?? "—",
          project_code: bl?.projects?.project_code ?? "",
          description: bl?.description ?? "—",
          phase: bl?.phase ?? "",
          hours: Number(a.hours ?? 0),
          task_status: a.task_status ?? "",
          estimated_deadline: a.estimated_deadline ?? "",
          notes: a.notes ?? null,
          user_id: a.user_id,
          user_name: a.users?.full_name ?? userMap[a.user_id] ?? "Ukjent",
        };
      };

      const merged = [...(ownTasks ?? []).map(mapRow), ...pmTasks.map(mapRow)];
      // Sorter på dato
      return merged.sort((a, b) => a.estimated_deadline.localeCompare(b.estimated_deadline));
    },
    staleTime: 60_000,
  });
}
